import {
  ContainerFlow,
  YardMoveCategory,
  type YardMovesResponse,
} from '@/api/generated.schemas';
import type { RegionConfig } from '@/components/charts/types';
import {
  attributeToSelection,
  filterMovements,
  groupMinorVisits,
  OTHER_VISITS_KEY,
  pivotPanelRows,
  type StackedSegment,
  stackPanelRows,
  type YardMovesFilters,
  type YardMovesPanelData,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import {
  buildWeekendRegions,
  colorForId,
} from '@/routes/(app)/(scenarios)/utils/scenario.utils';

// Helpers that need `@/` imports live here; `yard-moves.utils.ts` stays
// alias-free so the node test runner can load it.

const UNSELECTED_VISIT_OPACITY = 0.6;
const OTHER_VISITS_OPACITY = 0.35;

/** Chart filter state: `YardMovesFilters` plus the visible panels. */
export interface YardMovesChartFilters extends YardMovesFilters {
  categories: Set<YardMoveCategory>;
  containerFlows: Set<ContainerFlow>;
  selectedVisitIds: Array<string>;
}

export const DEFAULT_YARD_MOVES_FILTERS: YardMovesChartFilters = {
  categories: new Set(Object.values(YardMoveCategory)),
  containerFlows: new Set(Object.values(ContainerFlow)),
  selectedVisitIds: [],
  showSelectedOnly: false,
};

export type VesselNameById = Record<string, string | null | undefined>;

export interface YardMovesPanel extends YardMovesPanelData {
  category: YardMoveCategory;
  groupedVisitIds: Array<string>;
  /** The selected visits (see `attributeToSelection`); empty when nothing is selected. */
  highlightedVisitIds: Set<string>;
  regions: Array<RegionConfig>;
  /** Per row (same order as `rows`), see `stackPanelRows`. */
  stacks: Array<Array<StackedSegment>>;
}

function visitColor({
  visitId,
  mutedColors = false,
}: {
  visitId: string;
  mutedColors?: boolean;
}): string {
  return visitId === OTHER_VISITS_KEY
    ? 'var(--muted-foreground)'
    : colorForId({ id: visitId, mutedColors });
}

interface SegmentStyle {
  fill: string;
  fillOpacity: number;
}

/**
 * Highlight mode: with a non-empty highlight set, other visits are muted and
 * "Other visits" fades further; highlighted visits (and everything when
 * nothing is selected) keep full colour.
 */
export function segmentStyle(
  visitId: string,
  highlighted: Set<string>
): SegmentStyle {
  if (highlighted.size === 0 || highlighted.has(visitId)) {
    return { fill: visitColor({ visitId }), fillOpacity: 1 };
  }
  return {
    fill: visitColor({ visitId, mutedColors: true }),
    fillOpacity:
      visitId === OTHER_VISITS_KEY
        ? OTHER_VISITS_OPACITY
        : UNSELECTED_VISIT_OPACITY,
  };
}

/** One panel per visible category: filter → attribute to selection → group minor visits → pivot → stack → weekend regions. */
export function buildYardMovesPanels(
  data: YardMovesResponse,
  filters: YardMovesChartFilters
): Array<YardMovesPanel> {
  const highlighted = new Set(filters.selectedVisitIds);
  const filtered = attributeToSelection(
    filterMovements(data.movements, filters),
    highlighted
  );
  const categories = Object.values(YardMoveCategory).filter((category) =>
    filters.categories.has(category)
  );

  return categories.map((category) => {
    const categoryMovements = filtered.filter(
      (movement) => movement.category === category
    );
    const { movements, groupedVisitIds } = groupMinorVisits(
      categoryMovements,
      highlighted
    );
    const panel = pivotPanelRows(movements, data.shifts);
    return {
      category,
      ...panel,
      groupedVisitIds,
      highlightedVisitIds: highlighted,
      regions: buildWeekendRegions(panel.rows),
      stacks: stackPanelRows(panel.rows, panel.visitIds),
    };
  });
}
